import { useEffect, useState } from "react";
import axios from "axios";
import SeoHead from "@/components/public/SeoHead";
import Breadcrumb from "@/components/public/Breadcrumb";
import FadeIn from "@/components/public/FadeIn";
import GoogleReviewCard from "@/components/public/GoogleReviewCard";
import ReviewsSummary from "@/components/public/ReviewsSummary";
import CTASection from "@/components/public/CTASection";
import { useReviewStats } from "@/hooks/useReviewStats";
import { ExternalLink, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_REVIEW_URL = "https://g.page/r/CTTcLCQur3ryEAE/review";
const GOOGLE_PAGE_URL = "https://g.page/r/CTTcLCQur3ryEAE";

export default function AvisClientsPage() {
  const [avis, setAvis] = useState([]);
  const [stats, setStats] = useState(null);
  const reviewStats = useReviewStats();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/avis`),
      axios.get(`${API}/avis/stats`),
    ]).then(([a, s]) => {
      setAvis(a.data);
      setStats(s.data);
    });
  }, []);

  return (
    <div data-testid="avis-clients-page">
      <SeoHead
        title="Avis clients – 5 étoiles Google"
        description={`Avis clients vérifiés, note 5.0/5 sur Google avec ${reviewStats.count} avis. Découvrez les témoignages de nos clients en Suisse romande. Nettoyage Expert.`}
        path="/avis-clients"
        breadcrumbs={[{ name: "Accueil", url: "/" }, { name: "Avis clients" }]}
      />
      <Breadcrumb items={[{ name: "Accueil", url: "/" }, { name: "Avis clients" }]} />

      {/* Hero */}
      <section className="bg-[#062133] py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <FadeIn>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight" data-testid="avis-h1">
              Avis clients Nettoyage Expert
            </h1>
            <p className="mt-4 text-white/60 max-w-xl mx-auto">
              Tous nos avis proviennent de Google Business Profile et sont vérifiables publiquement.
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Stats Summary */}
      <section className="py-12 sm:py-16 bg-white border-b border-[#062133]/5">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <ReviewsSummary stats={stats} />
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <a href={GOOGLE_PAGE_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 border border-[#062133]/15 rounded-lg text-sm font-medium text-[#062133]/70 hover:bg-[#F4F6F8] transition-colors"
              data-testid="google-view-link">
              Voir tous les avis sur Google <ExternalLink size={14} />
            </a>
            <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 px-5 py-2.5 bg-[#336DBF] rounded-lg text-sm font-medium text-white hover:bg-[#2A5A9E] transition-colors"
              data-testid="google-leave-review">
              Laisser un avis <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="py-16 sm:py-24 bg-[#F4F6F8]" data-testid="avis-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-sm text-[#062133]/50 uppercase tracking-widest font-medium mb-2">Derniers avis vérifiés</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight">Ce que disent nos clients</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {avis.map((a, i) => (
              <FadeIn key={a.id || i} delay={i * 40}>
                <GoogleReviewCard avis={a} index={i} />
              </FadeIn>
            ))}
          </div>
          {/* CTA to see all reviews on Google */}
          <div className="text-center mt-10">
            <a href={GOOGLE_PAGE_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white rounded-xl border border-[#062133]/10 text-sm font-semibold text-[#062133] hover:shadow-md transition-all"
              data-testid="see-all-google">
              <Star size={16} className="text-amber-400 fill-amber-400" />
              Voir les {reviewStats.count} avis sur Google Maps
              <ExternalLink size={14} className="text-[#062133]/40" />
            </a>
          </div>
        </div>
      </section>

      <CTASection title="Rejoignez nos clients satisfaits" subtitle="Devis gratuit, résultat garanti, réponse sous 2 heures." />
    </div>
  );
}
